import { useState, type ReactNode } from 'react'
import { CalendarIcon, Check, ChevronsUpDown, X } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { FieldOption } from '@/types/github'

export function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[96px_1fr] items-center gap-2">
      <span className="text-muted-foreground text-xs">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

const NONE = '__none__'

export function SelectField({
  value,
  options,
  onChange,
  saving,
  clearable,
  renderOption,
}: {
  value: string | null
  options: FieldOption[]
  onChange: (value: string | null) => void
  saving?: boolean
  clearable?: boolean
  renderOption?: (name: string) => ReactNode
}) {
  return (
    <div className="flex items-center gap-1">
      <Select
        value={value ?? (clearable ? NONE : undefined)}
        onValueChange={(v) => onChange(v === NONE ? null : v)}
      >
        <SelectTrigger size="sm" className={cn('h-7 min-w-0 flex-1 text-xs', saving && 'field-saving')}>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {clearable && (
            <SelectItem value={NONE}>
              <span className="text-muted-foreground">None</span>
            </SelectItem>
          )}
          {options.map((o) => (
            <SelectItem key={o.id} value={o.name}>
              {renderOption ? renderOption(o.name) : o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {clearable && value != null && (
        <Button
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          title="Clear"
          aria-label="Clear"
          onClick={() => onChange(null)}
        >
          <X className="size-3" />
        </Button>
      )}
    </div>
  )
}

export function ComboboxField({
  value,
  options,
  onChange,
  saving,
  placeholder = 'Select…',
  modal = false,
}: {
  value: string | null
  options: string[]
  onChange: (value: string | null) => void
  saving?: boolean
  placeholder?: string
  modal?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className={cn('h-7 w-full justify-between font-mono text-xs font-normal', saving && 'field-saving')}
        >
          {value ?? <span className="text-muted-foreground">{placeholder}</span>}
          <ChevronsUpDown className="size-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>Nothing found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onChange(null)
                  setOpen(false)
                }}
              >
                <Check className={cn('size-3.5', value !== null && 'invisible')} />
                <span className="text-muted-foreground">None</span>
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => {
                    onChange(option)
                    setOpen(false)
                  }}
                >
                  <Check className={cn('size-3.5', value !== option && 'invisible')} />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function MultiComboboxField({
  values,
  options,
  onChange,
  saving,
  placeholder = 'None',
  renderValue,
  modal = false,
}: {
  values: string[]
  options: string[]
  onChange: (values: string[]) => void
  saving?: boolean
  placeholder?: string
  renderValue?: (values: string[]) => ReactNode
  modal?: boolean
}) {
  const [open, setOpen] = useState(false)
  const toggle = (option: string) => {
    onChange(
      values.includes(option) ? values.filter((v) => v !== option) : [...values, option],
    )
  }
  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className={cn('h-7 w-full justify-between text-xs font-normal', saving && 'field-saving')}
        >
          <span className="truncate">
            {values.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : renderValue ? (
              renderValue(values)
            ) : (
              values.join(', ')
            )}
          </span>
          <ChevronsUpDown className="size-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" />
          <CommandList>
            <CommandEmpty>Nothing found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem key={option} value={option} onSelect={() => toggle(option)}>
                  <Check className={cn('size-3.5', !values.includes(option) && 'invisible')} />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function DateField({
  value,
  onChange,
  saving,
}: {
  value: string | null
  onChange: (value: string | null) => void
  saving?: boolean
}) {
  const [open, setOpen] = useState(false)
  const date = value ? new Date(value + 'T00:00:00') : undefined
  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'h-7 flex-1 justify-start font-mono text-xs font-normal',
              !value && 'text-muted-foreground',
              saving && 'field-saving',
            )}
          >
            <CalendarIcon className="size-3" />
            {date ? format(date, 'yyyy-MM-dd') : 'Set date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => {
              onChange(d ? format(d, 'yyyy-MM-dd') : null)
              setOpen(false)
            }}
          />
        </PopoverContent>
      </Popover>
      {value && (
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          title="Clear date"
          aria-label="Clear date"
          onClick={() => onChange(null)}
        >
          <X className="size-3" />
        </Button>
      )}
    </div>
  )
}
